import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, History, ChevronDown, ChevronRight } from 'lucide-react';
import { Section } from '../FormFields';
import { colorForLabel } from '../../../utils/colorHash';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Manager-only, region-scoped counterpart to the Admin's all-region Weekly
// Inbound Digest — a Manager can opt into their OWN summary without ever
// seeing another region's lead volume/source data (region_health scopes
// everything server-side by session role+regions already).
const ManagerDigestSubscription = () => {
  const [optedIn, setOptedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);
  const [sendLog, setSendLog] = useState([]);
  const [expandedLogKey, setExpandedLogKey] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/my-subscription`, { withCredentials: true })
      .then((res) => setOptedIn(!!res.data.opted_in))
      .catch(() => toast.error('Failed to load your digest subscription status'))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (checked) => {
    setOptedIn(checked);
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/my-subscription`, { opted_in: checked }, { withCredentials: true });
      toast.success(checked ? "You'll get a weekly summary of your region(s)" : 'Weekly digest emails turned off');
    } catch (err) {
      setOptedIn(!checked);
      toast.error(err.response?.data?.detail || 'Failed to update your subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePreview = async () => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    setPreviewing(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/my-preview`, { withCredentials: true });
      setPreview(res.data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load your digest preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleToggleLog = async () => {
    if (logOpen) {
      setLogOpen(false);
      return;
    }
    setLoadingLog(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/my-log`, { withCredentials: true });
      setSendLog(res.data.log || []);
      setLogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load your digest history');
    } finally {
      setLoadingLog(false);
    }
  };

  if (loading) return null;

  return (
    <Section
      title="Weekly Digest"
      description="A weekly summary of inbound leads for your own region(s) only — never other regions' data."
    >
      <div data-testid="manager-digest-subscription" className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-slate-700" htmlFor="manager-digest-optin-checkbox">
          <input
            id="manager-digest-optin-checkbox"
            type="checkbox"
            checked={optedIn}
            disabled={saving}
            onChange={(e) => handleToggle(e.target.checked)}
            data-testid="manager-digest-optin-checkbox"
          />
          Email me a weekly summary of my region(s)
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleTogglePreview}
            disabled={previewing}
            data-testid="manager-digest-preview-button"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
          >
            <Mail className="w-3.5 h-3.5" /> {previewing ? 'Loading…' : previewOpen ? 'Hide preview' : 'Preview my digest'}
          </button>
          <button
            type="button"
            onClick={handleToggleLog}
            disabled={loadingLog}
            data-testid="manager-digest-history-button"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
          >
            <History className="w-3.5 h-3.5" /> {loadingLog ? 'Loading…' : logOpen ? 'Hide history' : 'My send history'}
          </button>
        </div>
        {previewOpen && preview && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs" data-testid="manager-digest-preview-panel">
            <p className="font-semibold text-slate-700 mb-1">Subject: {preview.subject}</p>
            <p className="text-slate-600 whitespace-pre-wrap">{preview.body}</p>
          </div>
        )}
        {logOpen && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-2" data-testid="manager-digest-log-panel">
            {sendLog.length === 0 && <p className="text-slate-400 italic">No digest has been sent to you yet.</p>}
            {sendLog.map((entry, idx) => {
              const logKey = entry.sent_at || idx;
              const isExpanded = expandedLogKey === logKey;
              return (
                <div key={logKey} className="border-b border-slate-200 last:border-0 pb-2 last:pb-0">
                  <button
                    type="button"
                    onClick={() => setExpandedLogKey(isExpanded ? null : logKey)}
                    data-testid={`manager-digest-log-entry-${idx}`}
                    className="flex flex-wrap items-center gap-2 w-full text-left"
                  >
                    {isExpanded ? <ChevronDown className="w-3 h-3 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                    <span className="font-medium text-slate-700">{new Date(entry.sent_at).toLocaleString('en-US', { timeZone: 'Asia/Manila' })}</span>
                    <span className={entry.sent ? 'text-emerald-600' : entry.suppressed ? 'text-amber-600' : 'text-red-500'}>
                      {entry.sent ? 'Sent' : entry.suppressed ? 'Suppressed (Test Mode)' : 'Not sent'}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{(entry.region_totals || []).reduce((a, r) => a + (r.total || 0), 0)} total leads</span>
                  </button>
                  {isExpanded && (
                    <div className="mt-2 ml-5 space-y-1" data-testid={`manager-digest-log-detail-${idx}`}>
                      {(entry.region_totals || []).map((r) => (
                        <div key={r.region} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorForLabel(r.region) }} />
                          <span className="text-slate-600 flex-1">{r.name}</span>
                          <span className="font-medium text-slate-700">{r.total}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Section>
  );
};

export default ManagerDigestSubscription;
